import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Send, CheckCircle, Users, Calendar } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    currentWebsite: "",
    websiteType: "",
    description: "",
    // Product/E-commerce fields
    productType: "",
    productUniqueness: "",
    productTargetCustomer: "",
    productFeatures: "",
    productPricing: "",
    productCompetition: "",
    productShowcase: "",
    productCallToAction: "",
    productTrust: "",
    // Service fields
    serviceType: "",
    serviceProcess: "",
    serviceResults: "",
    serviceExperience: "",
    serviceAreas: "",
    serviceApproach: "",
    serviceConsultation: "",
    servicePricing: "",
    serviceBooking: "",
    // Startup/Landing page fields
    landingSource: "",
    landingGoal: "",
    landingOffer: "",
    landingHeadline: "",
    landingObjections: "",
    landingUrgency: "",
    landingVariants: "",
    landingTracking: "",
    landingFollowUp: "",
    // Rebrand/Modernization fields
    rebrandProblems: "",
    rebrandKeepFunctionality: "",
    rebrandNewFeatures: "",
    rebrandGoal: "",
    rebrandCompetitors: "",
    rebrandBrandChanges: "",
    rebrandContentUpdate: "",
    rebrandTimeline: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: formData,
      });

      if (error) throw error;

      toast({
        title: t('contact.requestSubmitted'),
        description: t('contact.responseMessage'),
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        currentWebsite: "",
        websiteType: "",
        description: "",
        productType: "",
        productUniqueness: "",
        productTargetCustomer: "",
        productFeatures: "",
        productPricing: "",
        productCompetition: "",
        productShowcase: "",
        productCallToAction: "",
        productTrust: "",
        serviceType: "",
        serviceProcess: "",
        serviceResults: "",
        serviceExperience: "",
        serviceAreas: "",
        serviceApproach: "",
        serviceConsultation: "",
        servicePricing: "",
        serviceBooking: "",
        landingSource: "",
        landingGoal: "",
        landingOffer: "",
        landingHeadline: "",
        landingObjections: "",
        landingUrgency: "",
        landingVariants: "",
        landingTracking: "",
        landingFollowUp: "",
        rebrandProblems: "",
        rebrandKeepFunctionality: "",
        rebrandNewFeatures: "",
        rebrandGoal: "",
        rebrandCompetitors: "",
        rebrandBrandChanges: "",
        rebrandContentUpdate: "",
        rebrandTimeline: ""
      });
    } catch (error: any) {
      console.error('Error sending contact form:', error);
      toast({
        title: "Chyba",
        description: "Nepodarilo sa odoslať formulár. Skúste to prosím znova.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contact" className="py-12 md:py-16 lg:py-20 relative overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(/benefits-bg.png)' }} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-outline text-accent mb-4 md:mb-6 animate-fade-in uppercase px-2 leading-tight" style={{ fontWeight: 900 }}>
              {t('contact.title')}
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-white font-medium max-w-3xl mx-auto animate-fade-in text-center px-2" style={{ animationDelay: '100ms' }}>
              {t('contact.subtitle')}
            </p>
          </div>

          {/* Social Proof */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 mb-6 md:mb-8 animate-fade-in" style={{ animationDelay: '200ms' }}>
            <div className="flex items-center gap-2 text-white/90">
              <Calendar className="w-5 h-5 text-accent" />
              <span className="text-sm md:text-base font-medium">{t('contact.socialProof.since')}</span>
            </div>
            <div className="w-px h-6 bg-white/20 hidden md:block" />
            <div className="flex items-center gap-2 text-white/90">
              <Users className="w-5 h-5 text-accent" />
              <span className="text-sm md:text-base font-medium">{t('contact.socialProof.clients')}</span>
            </div>
          </div>

          <div className="bg-card/95 backdrop-blur-sm rounded-3xl p-5 md:p-8 lg:p-12 shadow-elegant border border-border/20">
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-base font-medium">
                    {t('contact.name')} *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder={t('contact.namePlaceholder')}
                    required
                    className="h-12"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base font-medium">
                    {t('contact.email')} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder={t('contact.emailPlaceholder')}
                    required
                    className="h-12"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-base font-medium">
                    {t('contact.phone')}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder={t('contact.phonePlaceholder')}
                    className="h-12"
                  />
                </div>

                {/* Current Website */}
                <div className="space-y-2">
                  <Label htmlFor="currentWebsite" className="text-base font-medium">
                    {t('contact.currentWebsite')}
                  </Label>
                  <Input
                    id="currentWebsite"
                    type="url"
                    value={formData.currentWebsite}
                    onChange={(e) => handleInputChange("currentWebsite", e.target.value)}
                    placeholder={t('contact.currentWebsitePlaceholder')}
                    className="h-12"
                  />
                </div>
              </div>

              {/* Website Type */}
              <div className="space-y-2">
                <Label htmlFor="websiteType" className="text-base font-medium">
                  {t('contact.websiteType')} *
                </Label>
                <Select value={formData.websiteType} onValueChange={(value) => handleInputChange("websiteType", value)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder={t('contact.websiteTypePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ecommerce">{t('contact.ecommerceWebsite')}</SelectItem>
                    <SelectItem value="services">{t('contact.servicesWebsite')}</SelectItem>
                    <SelectItem value="startup">{t('contact.startupWebsite')}</SelectItem>
                    <SelectItem value="rebrand">{t('contact.rebrandWebsite')}</SelectItem>
                    <SelectItem value="other">{t('contact.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-base font-medium">
                  {t('contact.description')} *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  placeholder={t('contact.descriptionPlaceholder')}
                  rows={5}
                  required
                  className="resize-none"
                />
              </div>

              {/* E-commerce/Product website specific questions */}
              {formData.websiteType === "ecommerce" && (
                <div className="space-y-4 md:space-y-6 pt-4 md:pt-6 border-t border-border/20">
                  {/* Basic Product Info */}
                  <div className="bg-secondary/20 rounded-lg p-4 md:p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.product.basicInfo')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="productType">{t('contact.product.type')}</Label>
                      <Input
                        id="productType"
                        value={formData.productType}
                        onChange={(e) => handleInputChange("productType", e.target.value)}
                        placeholder={t('contact.product.typePlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="productUniqueness">{t('contact.product.uniqueness')}</Label>
                      <Textarea
                        id="productUniqueness"
                        value={formData.productUniqueness}
                        onChange={(e) => handleInputChange("productUniqueness", e.target.value)}
                        placeholder={t('contact.product.uniquenessPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="productTargetCustomer">{t('contact.product.targetCustomer')}</Label>
                      <Textarea
                        id="productTargetCustomer"
                        value={formData.productTargetCustomer}
                        onChange={(e) => handleInputChange("productTargetCustomer", e.target.value)}
                        placeholder={t('contact.product.targetCustomerPlaceholder')}
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="bg-secondary/20 rounded-lg p-4 md:p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.product.productDetails')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="productFeatures">{t('contact.product.features')}</Label>
                      <Textarea
                        id="productFeatures"
                        value={formData.productFeatures}
                        onChange={(e) => handleInputChange("productFeatures", e.target.value)}
                        placeholder={t('contact.product.featuresPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="productPricing">{t('contact.product.pricing')}</Label>
                      <Input
                        id="productPricing"
                        value={formData.productPricing}
                        onChange={(e) => handleInputChange("productPricing", e.target.value)}
                        placeholder={t('contact.product.pricingPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="productCompetition">{t('contact.product.competition')}</Label>
                      <Input
                        id="productCompetition"
                        value={formData.productCompetition}
                        onChange={(e) => handleInputChange("productCompetition", e.target.value)}
                        placeholder={t('contact.product.competitionPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Product Presentation */}
                  <div className="bg-secondary/20 rounded-lg p-4 md:p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.product.presentation')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="productShowcase">{t('contact.product.showcase')}</Label>
                      <Textarea
                        id="productShowcase"
                        value={formData.productShowcase}
                        onChange={(e) => handleInputChange("productShowcase", e.target.value)}
                        placeholder={t('contact.product.showcasePlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="productCallToAction">{t('contact.product.callToAction')}</Label>
                      <Input
                        id="productCallToAction"
                        value={formData.productCallToAction}
                        onChange={(e) => handleInputChange("productCallToAction", e.target.value)}
                        placeholder={t('contact.product.callToActionPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="productTrust">{t('contact.product.trust')}</Label>
                      <Textarea
                        id="productTrust"
                        value={formData.productTrust}
                        onChange={(e) => handleInputChange("productTrust", e.target.value)}
                        placeholder={t('contact.product.trustPlaceholder')}
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Services/Freelancer website specific questions */}
              {formData.websiteType === "services" && (
                <div className="space-y-6 pt-6 border-t border-border/20">
                  {/* Basic Service Info */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.service.basicInfo')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="serviceType">{t('contact.service.type')}</Label>
                      <Input
                        id="serviceType"
                        value={formData.serviceType}
                        onChange={(e) => handleInputChange("serviceType", e.target.value)}
                        placeholder={t('contact.service.typePlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="serviceProcess">{t('contact.service.process')}</Label>
                      <Textarea
                        id="serviceProcess"
                        value={formData.serviceProcess}
                        onChange={(e) => handleInputChange("serviceProcess", e.target.value)}
                        placeholder={t('contact.service.processPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="serviceResults">{t('contact.service.results')}</Label>
                      <Textarea
                        id="serviceResults"
                        value={formData.serviceResults}
                        onChange={(e) => handleInputChange("serviceResults", e.target.value)}
                        placeholder={t('contact.service.resultsPlaceholder')}
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Service Positioning */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.service.positioning')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="serviceExperience">{t('contact.service.experience')}</Label>
                      <Textarea
                        id="serviceExperience"
                        value={formData.serviceExperience}
                        onChange={(e) => handleInputChange("serviceExperience", e.target.value)}
                        placeholder={t('contact.service.experiencePlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="serviceAreas">{t('contact.service.areas')}</Label>
                      <Input
                        id="serviceAreas"
                        value={formData.serviceAreas}
                        onChange={(e) => handleInputChange("serviceAreas", e.target.value)}
                        placeholder={t('contact.service.areasPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="serviceApproach">{t('contact.service.approach')}</Label>
                      <Textarea
                        id="serviceApproach"
                        value={formData.serviceApproach}
                        onChange={(e) => handleInputChange("serviceApproach", e.target.value)}
                        placeholder={t('contact.service.approachPlaceholder')}
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Booking & Contact */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.service.booking')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="serviceConsultation">{t('contact.service.consultation')}</Label>
                      <Input
                        id="serviceConsultation"
                        value={formData.serviceConsultation}
                        onChange={(e) => handleInputChange("serviceConsultation", e.target.value)}
                        placeholder={t('contact.service.consultationPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="servicePricing">{t('contact.service.pricing')}</Label>
                      <Input
                        id="servicePricing"
                        value={formData.servicePricing}
                        onChange={(e) => handleInputChange("servicePricing", e.target.value)}
                        placeholder={t('contact.service.pricingPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="serviceBooking">{t('contact.service.bookingMethod')}</Label>
                      <Input
                        id="serviceBooking"
                        value={formData.serviceBooking}
                        onChange={(e) => handleInputChange("serviceBooking", e.target.value)}
                        placeholder={t('contact.service.bookingMethodPlaceholder')}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Startup/Landing page specific questions */}
              {formData.websiteType === "startup" && (
                <div className="space-y-6 pt-6 border-t border-border/20">
                  {/* Basic Landing Page Info */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.landing.basicInfo')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="landingSource">{t('contact.landing.source')}</Label>
                      <Input
                        id="landingSource"
                        value={formData.landingSource}
                        onChange={(e) => handleInputChange("landingSource", e.target.value)}
                        placeholder={t('contact.landing.sourcePlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="landingGoal">{t('contact.landing.goal')}</Label>
                      <Input
                        id="landingGoal"
                        value={formData.landingGoal}
                        onChange={(e) => handleInputChange("landingGoal", e.target.value)}
                        placeholder={t('contact.landing.goalPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="landingOffer">{t('contact.landing.offer')}</Label>
                      <Textarea
                        id="landingOffer"
                        value={formData.landingOffer}
                        onChange={(e) => handleInputChange("landingOffer", e.target.value)}
                        placeholder={t('contact.landing.offerPlaceholder')}
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Optimization & Conversion */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.landing.optimization')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="landingHeadline">{t('contact.landing.headline')}</Label>
                      <Input
                        id="landingHeadline"
                        value={formData.landingHeadline}
                        onChange={(e) => handleInputChange("landingHeadline", e.target.value)}
                        placeholder={t('contact.landing.headlinePlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="landingObjections">{t('contact.landing.objections')}</Label>
                      <Textarea
                        id="landingObjections"
                        value={formData.landingObjections}
                        onChange={(e) => handleInputChange("landingObjections", e.target.value)}
                        placeholder={t('contact.landing.objectionsPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="landingUrgency">{t('contact.landing.urgency')}</Label>
                      <Input
                        id="landingUrgency"
                        value={formData.landingUrgency}
                        onChange={(e) => handleInputChange("landingUrgency", e.target.value)}
                        placeholder={t('contact.landing.urgencyPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Testing & Analytics */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.landing.testing')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="landingVariants">{t('contact.landing.variants')}</Label>
                      <Input
                        id="landingVariants"
                        value={formData.landingVariants}
                        onChange={(e) => handleInputChange("landingVariants", e.target.value)}
                        placeholder={t('contact.landing.variantsPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="landingTracking">{t('contact.landing.tracking')}</Label>
                      <Input
                        id="landingTracking"
                        value={formData.landingTracking}
                        onChange={(e) => handleInputChange("landingTracking", e.target.value)}
                        placeholder={t('contact.landing.trackingPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="landingFollowUp">{t('contact.landing.followUp')}</Label>
                      <Input
                        id="landingFollowUp"
                        value={formData.landingFollowUp}
                        onChange={(e) => handleInputChange("landingFollowUp", e.target.value)}
                        placeholder={t('contact.landing.followUpPlaceholder')}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Rebrand/Modernization specific questions */}
              {formData.websiteType === "rebrand" && (
                <div className="space-y-6 pt-6 border-t border-border/20">
                  {/* Current State */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.rebrand.currentState')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="rebrandProblems">{t('contact.rebrand.problems')}</Label>
                      <Textarea
                        id="rebrandProblems"
                        value={formData.rebrandProblems}
                        onChange={(e) => handleInputChange("rebrandProblems", e.target.value)}
                        placeholder={t('contact.rebrand.problemsPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rebrandKeepFunctionality">{t('contact.rebrand.keepFunctionality')}</Label>
                      <Textarea
                        id="rebrandKeepFunctionality"
                        value={formData.rebrandKeepFunctionality}
                        onChange={(e) => handleInputChange("rebrandKeepFunctionality", e.target.value)}
                        placeholder={t('contact.rebrand.keepFunctionalityPlaceholder')}
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* New Requirements */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.rebrand.newRequirements')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="rebrandNewFeatures">{t('contact.rebrand.newFeatures')}</Label>
                      <Textarea
                        id="rebrandNewFeatures"
                        value={formData.rebrandNewFeatures}
                        onChange={(e) => handleInputChange("rebrandNewFeatures", e.target.value)}
                        placeholder={t('contact.rebrand.newFeaturesPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rebrandGoal">{t('contact.rebrand.goal')}</Label>
                      <Textarea
                        id="rebrandGoal"
                        value={formData.rebrandGoal}
                        onChange={(e) => handleInputChange("rebrandGoal", e.target.value)}
                        placeholder={t('contact.rebrand.goalPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rebrandCompetitors">{t('contact.rebrand.competitors')}</Label>
                      <Input
                        id="rebrandCompetitors"
                        value={formData.rebrandCompetitors}
                        onChange={(e) => handleInputChange("rebrandCompetitors", e.target.value)}
                        placeholder={t('contact.rebrand.competitorsPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* Brand & Content */}
                  <div className="bg-secondary/20 rounded-lg p-6 space-y-4">
                    <h4 className="font-medium text-card-foreground uppercase">{t('contact.rebrand.brandContent')}</h4>
                    
                    <div className="space-y-2">
                      <Label htmlFor="rebrandBrandChanges">{t('contact.rebrand.brandChanges')}</Label>
                      <Input
                        id="rebrandBrandChanges"
                        value={formData.rebrandBrandChanges}
                        onChange={(e) => handleInputChange("rebrandBrandChanges", e.target.value)}
                        placeholder={t('contact.rebrand.brandChangesPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rebrandContentUpdate">{t('contact.rebrand.contentUpdate')}</Label>
                      <Input
                        id="rebrandContentUpdate"
                        value={formData.rebrandContentUpdate}
                        onChange={(e) => handleInputChange("rebrandContentUpdate", e.target.value)}
                        placeholder={t('contact.rebrand.contentUpdatePlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rebrandTimeline">{t('contact.rebrand.timeline')}</Label>
                      <Input
                        id="rebrandTimeline"
                        value={formData.rebrandTimeline}
                        onChange={(e) => handleInputChange("rebrandTimeline", e.target.value)}
                        placeholder={t('contact.rebrand.timelinePlaceholder')}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-center pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground px-12 py-6 text-lg rounded-full shadow-accent group"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t('contact.submitting')}
                    </>
                  ) : (
                    <>
                      {t('contact.submitRequest')}
                      <Send className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Trust indicators */}
            <div className="mt-8 pt-8 border-t border-border/20">
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-accent mr-2" />
                  {t('contact.freeConsultation')}
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-accent mr-2" />
                  {t('contact.responseTime')}
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-accent mr-2" />
                  {t('contact.noCommitment')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;